<script setup lang="ts">
import {DashboardWidgetProps} from "@/utils/presenters/interfaces.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import SideBarUtils from "@/utils/common/SideBarUtils.ts";
import ColorUtils from "@/utils/common/ColorUtils.ts";
import MenuTargetType from "@/utils/constants/MenuTargetType.ts";
import {computed} from "vue";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import Translator from "@/utils/common/Translator.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";

const props = defineProps<DashboardWidgetProps>();
const widgetId: string = (props.chartLine.widgetId ?? 'html');
const cardMenuItems = computed(() => {
    let lines = props.chartLine.cardMenu ?? [];
    return lines.flatMap(l => {
      if (l.target?.type === MenuTargetType.UserFavourites) {
        let favouriteItems = LoginUtils.loginInfo?.favourites?.favouriteItems;
        if (!favouriteItems)
          return [];
        return favouriteItems.map(f => SideBarUtils.toMenuItem(f));
      } else if (l.target?.type === MenuTargetType.AllReports) {
        let rootReportGroups = ClientCache.getRootReportGroups();
        if (!rootReportGroups)
          return [];
        return rootReportGroups.map(rg => SideBarUtils.convertReportGroupToMenuItem(rg));
      } else if (l.target?.type === MenuTargetType.AutoDashBoards) {
        let autoDashBoardGroups = ClientCache.getAutoDashBoardGroups();
        if (!autoDashBoardGroups)
          return [];
        return autoDashBoardGroups.flatMap(group => {
          let dashboardsOfGroup = ClientCache.getDashboardsOfGroup(group);
          return dashboardsOfGroup.map(dashboard => SideBarUtils.convertReportGroupToMenuItem(dashboard));
        });
      } else if (l.target?.type === MenuTargetType.DashboardsGroup) {
        let dashboardsOfGroup = ClientCache.getDashboardsOfGroup(l.target?.targetRecord);
        return dashboardsOfGroup.map(dashboard => SideBarUtils.convertReportGroupToMenuItem(dashboard));
      }
      return [l];
    });
  }
);
function bindButtonColors(color?: string): Record<any, any> {
  if (!color)
    return {
      text: "dark:tw-text-white"
    };
  return {
    icon: ColorUtils.isDarkColor(color) ? 'active-white' : 'active-black',
    text: ColorUtils.isDarkColor(color) ? '!tw-text-white' : '!tw-text-namaFontColor-light'
  }
}

function defaultIconByType(type?: string) {
  if (!type)
    return;
  switch (type) {
    case MenuTargetType.Link:
      return "link";
    case MenuTargetType.EntityList:
      return "nm-list-view";
    case MenuTargetType.InternalLink:
      return "nm-internal-link";
    case MenuTargetType.Report:
      return "nm-report";
    case MenuTargetType.NewRecord:
      return "nm-add-record";
    case MenuTargetType.Dashboard:
      return "nm-dashboards";
    case MenuTargetType.Record:
      return "nm-record";
  }
  return undefined;
}

const heading = Translator.calcByLanguage(props.chartLine.arChartTitle ?? '', props.chartLine.enChartTitle ?? '')

const isAdmin = computed(() => LoginUtils.isCurrentUserAdminOrTreatAsAdmin());

function openWidgetEditView() {
  if (!props.chartLine.widgetId) return;
  const link = PlaceUtils.editLink('DashBoardWidget', props.chartLine.widgetId);
  if (link) window.open(link, '_blank');
}

</script>

<template>
  <div :key="chartKey + widgetId" class="tw-h-full dark:tw-bg-namaBackground-bright-dark tw-bg-namaBackground-bright-light">
    <div v-if="heading || isAdmin"
         class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-p-3 md:tw-mb-4">
      <span v-if="heading"
            v-text="heading"
            class="tw-font-bold tw-text-2xl dark:tw-text-white"/>
      <a v-if="isAdmin && props.chartLine.widgetId"
         class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer tw-shrink-0"
         :title="'Open widget edit screen (admin)'"
         @click.stop="openWidgetEditView">
        <q-icon name="open_in_new" size="18px"/>
      </a>
    </div>
    <ul v-if="chartLine.displayCardMenusAsBullets"
        class="tw-list-disc tw-ps-2 tw-pe-3 tw-space-y-1 tw-py-3 tw-overflow-y-auto tw-max-h-full dark:tw-text-white">
      <li v-for="item in cardMenuItems"
          data-animate-box
          class="tw-text-center tw-list-none tw-rounded-md dark:tw-bg-white/10 tw-bg-black/10">
        <q-btn flat no-caps dense align="left"
               v-bind="{...SideBarUtils.createLinkPropsForMenuItem(item, true)}"
               :style="item.color ? { backgroundColor: item.color } : undefined"
               class="tw-w-full tw-text-center tw-ps-3 tw-rounded-md">
          <NamaIcon data-animate-badge :menuItem="item" class="tw-size-4 tw-me-2" :class="bindButtonColors(item.color)?.icon"
                    :defaultIcon="defaultIconByType(item?.target?.type)"/>
          <span data-animate-text v-text="SideBarUtils.translateMenuItem(item)" :class="bindButtonColors(item.color)?.text"/>
        </q-btn>
      </li>
    </ul>
    <div v-else class="tw-flex tw-flex-wrap tw-p-3 tw-overflow-y-auto tw-max-h-full">
      <div
          v-for="item in cardMenuItems"
          data-animate-box
          class="tw-grow tw-p-1 tw-flex tw-min-w-fit tw-w-full mobile:tw-basis-1/2 tw-basis-1/4 tw-min-h-16">
        <q-btn
          v-bind="{...SideBarUtils.createLinkPropsForMenuItem(item,true)}"
          class="tw-rounded-lg tw-min-w-full tw-py-2.5 tw-capitalize tw-h-full" :style="{backgroundColor:item.color}">
          <div class="tw-flex tw-flex-col tw-items-center tw-gap-1.5">
            <NamaIcon data-animate-badge :menuItem="item" class="tw-size-5" :class="bindButtonColors(item.color)?.icon"
                      :defaultIcon="defaultIconByType(item?.target?.type)"/>
            <span data-animate-text v-text="SideBarUtils.translateMenuItem(item)" :class="bindButtonColors(item.color)?.text"/>
          </div>
        </q-btn>
      </div>
    </div>
  </div>
</template>
